import {
  PORTAL_DESCRIPTION_MAX,
  PORTAL_TITLE_MAX,
  PORTAL_WEB_PATHS,
  type PortalRequestDetail,
} from '@fos/shared/contracts/portal';
import { CircleCheck } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input, Textarea } from '../../../ui/Input';
import { FULLSCREEN_ON_PHONE } from '../../mobile';

type Errors = Partial<Record<'title' | 'description', string>>;

/**
 * „Nowe zgłoszenie” w portalu klienta (DECISIONS 2026-10-17 [P] „Model portalu klienta” nr 4): tytuł, opis (co się
 * dzieje / czego potrzebujesz) i „Pilne” (priorytet wysoki u zespołu). Po wysłaniu to samo okno pokazuje
 * potwierdzenie „Zgłoszenie wysłane” z numerem i przyciskiem do szczegółów. Na telefonie na cały ekran.
 */
export function NewRequestDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const utils = trpc.useUtils();
  const navigate = useNavigate();
  const create = trpc.portal.createRequest.useMutation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<PortalRequestDetail | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setDescription('');
    setUrgent(false);
    setErrors({});
    setFormError(null);
    setCreated(null);
  }, [open]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const next: Errors = {};
    if (title.trim().length < 3) next.title = 'Wpisz krótki tytuł (co najmniej 3 znaki)';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    try {
      const saved = await create.mutateAsync({ title: title.trim(), description: description.trim(), urgent });
      utils.portal.getRequest.setData({ id: saved.id }, saved);
      void utils.portal.listRequests.invalidate();
      setCreated(saved);
    } catch (err) {
      const mapped: Errors = {};
      for (const issue of validationIssues(err)) {
        if ((issue.path === 'title' || issue.path === 'description') && !mapped[issue.path]) {
          mapped[issue.path] = issue.message;
        }
      }
      if (Object.keys(mapped).length > 0) setErrors(mapped);
      else setFormError(errorMessage(err));
    }
  };

  if (created) {
    return (
      // osobny klucz: nowe okno bez stanu „niezapisane zmiany” z formularza (useDirtyGuard)
      <Dialog
        key="created"
        open={open}
        onOpenChange={onOpenChange}
        title="Zgłoszenie wysłane"
        width="s"
        footer={
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Zamknij
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                onOpenChange(false);
                navigate(PORTAL_WEB_PATHS.request(created.id));
              }}
            >
              Zobacz zgłoszenie
            </Button>
          </>
        }
      >
        <div className="flex flex-col items-start gap-3">
          <span className="inline-flex size-10 items-center justify-center rounded-full bg-success-subtle text-success">
            <CircleCheck size={20} strokeWidth={1.75} aria-hidden />
          </span>
          <p className="text-body-s text-fg-secondary">
            Zgłoszenie <span className="font-medium text-fg">#{created.number}</span> trafiło do zespołu. Odpowiemy w
            rozmowie przy zgłoszeniu, a o odpowiedzi damy znać mailem.
          </p>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      key="form"
      open={open}
      onOpenChange={onOpenChange}
      title="Nowe zgłoszenie"
      description="Opisz krótko, co się dzieje albo czego potrzebujesz. Odpowiemy w rozmowie przy zgłoszeniu."
      width="l"
      className={FULLSCREEN_ON_PHONE}
    >
      <form ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-4" aria-label="Nowe zgłoszenie">
        <Field
          label="Tytuł"
          hint="Np. „Nie działa formularz kontaktowy” albo „Nowe zdjęcia w galerii”."
          error={errors.title}
        >
          <Input
            value={title}
            maxLength={PORTAL_TITLE_MAX}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            enterKeyHint="next"
          />
        </Field>
        <Field
          label="Opis"
          hint="Co się dzieje albo czego potrzebujesz? Gdzie (adres strony), od kiedy, co już sprawdzono."
          error={errors.description}
        >
          <Textarea
            rows={6}
            value={description}
            maxLength={PORTAL_DESCRIPTION_MAX}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <div>
          <Checkbox
            checked={urgent}
            onCheckedChange={setUrgent}
            touchTarget
            label={<span className="text-fg">Pilne — coś nie działa i przeszkadza w pracy</span>}
          />
        </div>
        {formError && <FormMessage>{formError}</FormMessage>}
        <div className="mt-2 flex flex-col-reverse gap-2 md:flex-row md:justify-end">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="max-md:h-11">
            Anuluj
          </Button>
          <Button type="submit" variant="primary" loading={create.isPending} className="max-md:h-11">
            Wyślij zgłoszenie
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
